import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ChatConversation, ChatUserDisplayMap } from 'lib-trend-core';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ChatNotificationGateService, ChatNotifyContext } from '../chat-preferences/chat-notification-gate.service';
import { ChatSocketBridgeService } from '../state/chat-socket-bridge.service';
import * as ChatActions from '../state/chat.actions';
import { ChatPushService } from './chat-push.service';
import {
  badgeTextSelector,
  isLoadingSelector,
  loadErrorSelector,
  sortedConversationsSelector,
  totalUnreadSelector,
  unreadCountMapSelector,
  userDisplaySelector,
} from '../state/chat.selectors';

/**
 * Internal Chat unread-badge + recent-conversation feed (SPEC-2026-07-internal-chat).
 *
 * T-17 introduced this service backed by local BehaviorSubjects; T-18 rebased
 * it on the NgRx `chatState` store (selectors + dispatched actions) as
 * designed. The public surface is UNCHANGED — the always-mounted shell
 * surfaces (chat bubble FAB, topbar pill in header.v2) keep consuming
 * `init()` / `refresh()` / `totalUnread$` / `badgeText$` / `conversations$` /
 * `unreadByChannel$` / `userDisplay$` / `loading$` / `loadError$` untouched.
 *
 * Room joins, socket streams and the bootstrap lifecycle now live in
 * ChatSocketBridgeService (provided by the eager ChatStateModule).
 */
@Injectable({ providedIn: 'root' })
export class ChatBadgeService {

  /** channelId → unread count (only channels with count > 0). */
  readonly unreadByChannel$: Observable<Record<string, number>>;

  /** Activity-sorted conversation snapshot (previews kept live by the store). */
  readonly conversations$: Observable<ChatConversation[]>;

  /** userId → { name, image } map hydrated by the bootstrap endpoint. */
  readonly userDisplay$: Observable<ChatUserDisplayMap>;

  readonly loading$: Observable<boolean>;
  readonly loadError$: Observable<boolean>;

  /** Total unread messages across every conversation. */
  readonly totalUnread$: Observable<number>;

  /** Badge label capped at "99+" — what the FAB/topbar pills render. */
  readonly badgeText$: Observable<string>;

  constructor(
    private readonly store: Store,
    private readonly chatSocketBridge: ChatSocketBridgeService,
    private readonly chatPushService: ChatPushService,
    private readonly notificationGate: ChatNotificationGateService,
  ) {
    this.unreadByChannel$ = this.store.select(unreadCountMapSelector);
    this.conversations$ = this.store.select(sortedConversationsSelector);
    this.userDisplay$ = this.store.select(userDisplaySelector);
    this.loading$ = this.store.select(isLoadingSelector);
    this.loadError$ = this.store.select(loadErrorSelector);
    this.totalUnread$ = this.store.select(totalUnreadSelector).pipe(distinctUntilChanged());
    this.badgeText$ = this.store.select(badgeTextSelector);
  }

  /**
   * Idempotent activation — called by the first shell surface that mounts
   * (the bubble host). Delegates to the socket bridge: personal + presence
   * room joins, socket → store streams, bootstrap load + reconnect refresh.
   */
  init(): void {
    this.chatSocketBridge.init();
    // FCM web-push activation (T-23) — idempotent, self-gated by moduleChat +
    // authentication + vapidKey, degrades silently when push is unavailable.
    this.chatPushService.init();
  }

  /** (Re)loads the bootstrap snapshot — also the retry path for load errors. */
  refresh(): void {
    this.store.dispatch(ChatActions.loadBootstrap());
  }

  /**
   * Notification gate for surfaces that raise in-app alerts (T-21 · RF-28/RF-36
   * · AC-24/AC-32): honors the per-conversation notification level ("Só menções"
   * alerts only for mentions and DMs, "Nada" never alerts) and quiet hours.
   * Unread counters are NEVER gated — only alerts are.
   *
   * The audible alert on incoming messages is already wired in
   * ChatSocketBridgeService. This re-export exists for toast raisers outside the
   * store layer — notably the FCM foreground toast in
   * `ChatPushService.handleForegroundMessage()` (owned by T-23), which needs one
   * line right after it reads `channelId`:
   *
   * ```ts
   * if (channelId && !this.chatBadgeService.canNotify(channelId)) return;
   * ```
   *
   * (or inject `ChatNotificationGateService` directly there to avoid the
   * ChatBadgeService → ChatPushService dependency direction).
   */
  canNotify(channelId: string, context: ChatNotifyContext = {}): boolean {
    return this.notificationGate.canNotify(channelId, context);
  }
}
